import React, { useState, useCallback } from 'react';
import { Button, Dropdown, message } from 'antd';
import { BugOutlined, CheckCircleOutlined, AlertOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import BugReportOverlay from './BugReportOverlay';
import VerifyModal from './VerifyModal';
import { bugReportApi } from '../../services/bugReportApi';
import { devApi } from '../../services/devApi';
import type { Annotation } from './types';

const routeToFormId: Record<string, string> = {
  '/sc': 'pwc202f1',
  '/product-grade': 'pw1118f1',
};

function getFormIdFromPath(path: string): string | null {
  // Exact match first
  if (routeToFormId[path]) return routeToFormId[path];
  // Prefix match
  for (const [route, formId] of Object.entries(routeToFormId)) {
    if (path.startsWith(route + '/')) return formId;
  }
  return null;
}

const BugReportButton: React.FC = () => {
  const location = useLocation();
  const [annotating, setAnnotating] = useState(false);
  const [verifyVisible, setVerifyVisible] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const formId = getFormIdFromPath(location.pathname);

  const handleStartAnnotation = useCallback(() => {
    setAnnotating(true);
  }, []);

  const handleCancelAnnotation = useCallback(() => {
    setAnnotating(false);
  }, []);

  const handleSubmitAnnotations = useCallback(async (annotations: Annotation[], screenshots: string[]) => {
    try {
      const user = localStorage.getItem('user') || sessionStorage.getItem('user');
      let reportedBy = 'anonymous';
      if (user) {
        try {
          const parsed = JSON.parse(user);
          reportedBy = parsed.userName || parsed.userId || 'anonymous';
        } catch { /* ignore */ }
      }

      await bugReportApi.create({
        formId: formId || undefined,
        page: location.pathname,
        pageTitle: document.title,
        reportedBy,
        browserInfo: navigator.userAgent,
        annotations,
        screenshots,
      });

      message.success(`已送出 ${annotations.length} 個問題回報`);
      setAnnotating(false);
    } catch (err) {
      message.error('送出失敗，請重試');
      console.error('Bug report submit error:', err);
    }
  }, [formId, location.pathname]);

  const handleVerify = useCallback(async () => {
    if (!formId) {
      message.warning('此頁面無對應模組');
      return;
    }

    setVerifyLoading(true);
    try {
      await devApi.updateModule(formId, { status: 'done' });
      message.success('驗收完成！模組已標記為完成');
      setVerifyVisible(false);
    } catch (err) {
      message.error('更新失敗，請重試');
      console.error('Verify error:', err);
    } finally {
      setVerifyLoading(false);
    }
  }, [formId]);

  const menuItems = [
    {
      key: 'report',
      label: '回報問題',
      icon: <AlertOutlined />,
      onClick: handleStartAnnotation,
    },
    {
      key: 'verify',
      label: '完成驗收',
      icon: <CheckCircleOutlined />,
      onClick: () => setVerifyVisible(true),
    },
  ];

  if (annotating) {
    return (
      <BugReportOverlay
        onSubmit={handleSubmitAnnotations}
        onCancel={handleCancelAnnotation}
      />
    );
  }

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        placement="topRight"
        trigger={['click']}
        overlayStyle={{ zIndex: 1501 }}
      >
        <Button
          type="primary"
          danger
          shape="circle"
          size="large"
          icon={<BugOutlined style={{ fontSize: 24 }} />}
          style={{
            position: 'fixed',
            right: 24,
            bottom: 24,
            zIndex: 1500,
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(255, 77, 79, 0.4)',
          }}
        />
      </Dropdown>

      <VerifyModal
        open={verifyVisible}
        formId={formId}
        pagePath={location.pathname}
        onConfirm={handleVerify}
        onCancel={() => setVerifyVisible(false)}
        loading={verifyLoading}
      />
    </>
  );
};

export default BugReportButton;
